/**
 * Marketing-page CMS defaults + provider.
 *
 * Everything the SaaS marketing landing renders is mirrored here so the page
 * still works (a) before the primary admin saves any overrides and (b) if the
 * /api/marketing-content fetch ever fails. The admin tab at /admin/marketing
 * lets the primary admin override any of these fields without a deploy.
 *
 * Editable from `<AdminMarketing />`. Read by `useMarketing()`.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

export const DEFAULT_MARKETING = {
  hero: {
    overline: "Built for listing agents",
    headline: "Stop managing listings through",
    headline_italic: "texts and emails.",
    subhead: "Give your sellers a better experience.",
    body: "PorchLight gives every seller a clear, branded place where they always know what's next — so you stop fielding the same questions every Sunday at 9pm.",
    cta_primary: "Start your first listing",
    cta_secondary: "See it in action first",
    social_proof: "Built by listing agents who got tired of the chaos.",
  },
  built_by_agent: [
    { stat: "Built by a working", emphasis: "real-estate agent." },
    { stat: "Used daily by", emphasis: "real sellers." },
    { stat: "First listing free, then $49 / month,", emphasis: "cancel anytime." },
  ],
  sound_familiar: {
    overline: "Sound familiar?",
    headline: "Most listings live in",
    headline_italic: "texts, emails, and your head.",
    body: "That's why even your best clients still feel anxious. PorchLight pulls every step into one branded place, so they feel guided — and you finally get your evenings back.",
    chaos: [
      "“What's next?” texts on Sunday at 9pm",
      "Forms scattered across email and PDFs",
      "Sellers losing track of important dates",
      "Repeating yourself five times a week",
    ],
    calm: [
      "One place for forms, dates, and updates",
      "Sellers know their next step before asking",
      "Auto-saved progress, branded as you",
      "Quiet evenings. Organized weeks.",
    ],
  },
  demo_video: {
    overline: "Demo video",
    headline: "See how PorchLight works",
    body: "A quick look at how agents can use PorchLight to create a more organized, professional seller experience.",
    badge: "90-second tour · coming soon",
    cta: "Or get a live walk-through →",
  },
  what_it_does: {
    overline: "What it does",
    headline: "Everything your sellers need, without the chaos.",
    body: "PorchLight helps agents give sellers a guided experience — branded with your name, your colors, your voice — so the work you actually do shines through.",
    items: [
      "A step-by-step selling experience",
      "Room-by-room home prep plans",
      "One place for forms, updates, and key information",
      "Clear offer comparisons clients can actually understand",
    ],
  },
  spotlights: [
    {
      id: "prep-guide-spotlight",
      overline: "Room-by-room prep",
      headline: "Room-by-room prep — not a generic checklist.",
      italic_tail: "not a generic checklist.",
      body: "Every seller gets a branded, room-specific guide with photo-prep tasks, lifestyle staging tips, and progress that flows back to you in real time. Add a custom task on any seller's plan in seconds — they see it instantly.",
      features: [
        "Room-by-room prep you control",
        "Mark items 'N/A' — adapts per home",
        "Sellers add their own tasks",
        "Auto-saved progress, every device",
        "Photo & showing prep built in",
        "Branded with your logo",
      ],
    },
    {
      id: "signing-spotlight",
      overline: "Forms and signing",
      headline: "Any form you use, signed in your portal.",
      italic_tail: "signed in your portal.",
      body: "Upload any blank PDF \u2014 disclosures, agreements, your own intake sheets \u2014 drop the fields where they belong, and choose which ones you fill and which ones your client does. They sign on their phone, in your branded portal, not a third party's. You get the finished PDF back with a record of who signed it, when, and against which document.",
      features: [
        "Upload any PDF and place the fields",
        "Typed signature and initials, adopted once",
        "Your client signs on their phone",
        "Signed PDF with a signing record",
      ],
    },
    {
      id: "listing-marketing-spotlight",
      overline: "Listing marketing",
      headline: "Every listing gets its own page, and the posts to go with it.",
      italic_tail: "and the posts to go with it.",
      body: "PorchLight builds a public website for the listing out of what your seller already filled in and the photos you already uploaded. No new data entry. Then it writes ready-to-post captions for the new listing, the open house, a price change and sold, from those same details. You edit them and post them yourself.",
      features: [
        "A public page for every listing",
        "Captions written from your listing details",
        "Photos uploaded once, reused everywhere",
        "You post them \u2014 PorchLight doesn't",
      ],
    },
    {
      id: "listing-activity-spotlight",
      overline: "Listing activity",
      headline: "Every showing, every comment, every view, where they can see it.",
      italic_tail: "where they can see it.",
      body: "Private showings and open houses, how many groups came through, what the buyer agents said, and how many people opened the listing page — it all lands in the seller portal as it happens. They stay informed without you writing another update email, and you still decide which feedback they see.",
      features: [
        "Private showings, logged in seconds",
        "Open houses, with groups through the door",
        "Buyer agent feedback, shared when you choose",
        "Listing page views, counted for you",
        "Upcoming visits, so they can plan around them",
        "All of it in their portal, not another email",
      ],
    },
    {
      id: "net-sheet-spotlight",
      overline: "Net sheet & offer comparison",
      headline: "Live math your sellers actually understand.",
      italic_tail: "actually understand.",
      body: "Stop walking sellers through hand-typed proceeds estimates over text. Punch in numbers and watch their net update live. Then drop in two or three offers and let the side-by-side comparison make the decision obvious.",
      features: [
        "Live proceeds calculator",
        "Side-by-side offer comparison",
        "Auto-saves as you type",
        "Save as branded PDF",
        "Per-offer concessions & commissions",
        "Pulls from listing details",
      ],
    },
    {
      id: "important-dates-spotlight",
      overline: "Important dates",
      headline: "Every milestone in one place — automatic reminders.",
      italic_tail: "automatic reminders.",
      body: "Strategy meeting, photo day, listing launch, open house, closing. Every date in one timeline, with reminder emails that go out automatically. Add custom dates per seller in two clicks.",
      features: [
        "Visual milestone timeline",
        "Auto reminder emails",
        "Add custom dates per seller",
        "Time-of-day support",
        "Smart 'Next up' card on dashboard",
        "Branded notifications",
      ],
    },
    {
      id: "agent-portal-spotlight",
      overline: "Your agent portal",
      headline: "And the dashboard you've been waiting for.",
      italic_tail: "you've been waiting for.",
      body: "While your sellers see their guided experience, you get a single command center for every listing. Customize each seller's prep, view their progress, message them, manage submissions, and own your brand — all in one tab.",
      features: [
        "Every seller in one dashboard",
        "Per-seller prep customization",
        "Inline messaging & inbox",
        "Form submission viewer",
        "Quick-start prep bundles",
        "Full white-label branding",
      ],
    },
  ],
  more_features: {
    overline: "And a whole lot more",
    headline: "Nine more features that make every listing",
    headline_italic: "look effortless.",
    body: "Those get the spotlight — but PorchLight is built end-to-end, from first appointment through closing.",
    items: [
      { title: "Documents locker",          body: "Upload disclosures, inspections, and contracts. Sellers tap, sellers view." },
      { title: "Smart seller forms",        body: "Listing input wizard, home story, showing prefs, inclusions — every form auto-saves and emails you a branded PDF when submitted." },
      { title: "Showings calendar",         body: "Schedule showings with auto-reminders 24 hours and 2 hours ahead — sellers always know when to be out." },
      { title: "Branded vendor directory",  body: "Your trusted painters, stagers, and lenders — visible to every seller." },
      { title: "Inline 'Ask [your name]' messaging", body: "Every form, room, and milestone has a built-in chat thread back to you — branded with your first name, not a generic 'Contact agent' button." },
      { title: "Quick-start prep bundles",  body: "Save your favorite custom prep tasks and apply them to a new seller in one click." },
      { title: "Branded PDF exports",       body: "Net sheets, prep guides, listing forms — every download wears your colors and logo." },
      { title: "AI-powered MLS auto-fill",  body: "Drop in a listing PDF; we extract the fields into your input form automatically." },
      { title: "Co-owner accounts",         body: "Add a spouse or partner so both sellers see the same plan and updates." },
    ],
  },
  why_agents: {
    overline: "Why agents use it",
    headline: "A better experience for your clients.",
    headline_italic: "Less chaos for you.",
    reasons: [
      "Win more listings — show up to appointments with a tool that proves you're more organized than the competition",
      "Help sellers feel guided instead of overwhelmed",
      "Stop repeating the same “what’s next?” answers",
      "Keep important details organized in one place",
      "Look more polished and professional from the first appointment",
      "Earn referrals from sellers who felt cared for at every step",
    ],
  },
  pricing: {
    overline: "Pricing",
    headline: "One simple plan.",
    headline_italic: "Your first listing is free.",
    body: "Take one listing all the way through and pay nothing. No card to start, no setup fees, cancel anytime.\nBring your logo, your colors, your voice - your sellers see your brand, not ours.",
    features_left: [
      "Unlimited sellers",
      "White-label branding (logo, colors, voice)",
      "Custom prep checklists & vendor directory",
      "Document locker & e-form submissions",
      "Showings calendar with auto-reminders",
    ],
    features_right: [
      "Net sheet & multi-offer comparison",
      "Branded email notifications",
      "Two months free on annual plans",
      "Priority onboarding & support",
      "AI-powered MLS PDF auto-fill",
    ],
    cta: "Start your first listing",
  },
  faq: {
    overline: "FAQ",
    headline: "Things agents",
    headline_italic: "always ask first.",
    body: "Don't see your question? Drop it in the form below — we'll answer it on the call.",
    cta: "Or just see it in action →",
    items: [
      { q: "How do my sellers actually use it?",
        a: "Each seller gets a private link to their own portal — no app to download. They log in once, see their prep guide, important dates, documents, and offer comparisons all branded as you. They can do everything from their phone." },
      { q: "Can I customize the prep guide for each seller?",
        a: "Yes — you can add custom tasks per seller (\"replace the front door,\" \"meet with painter Tuesday\"), hide items that don't apply (no fireplace, no yard), and even save a Quick-start bundle to apply your favorite custom tasks to a new seller in one click." },
      { q: "Will my brand actually show up everywhere?",
        a: "Logo, colors, your name, your headshot, your email signature — every page, every email, every PDF. Your sellers see your brand. PorchLight is white-label so it never feels like third-party software." },
      { q: "What about my existing tools — does this replace my CRM?",
        a: "No, PorchLight is purpose-built for the seller side of a transaction. It pairs nicely with your CRM (kvCORE, Follow Up Boss, etc.). You keep your CRM for lead gen and client database; PorchLight handles the listing-management work that your CRM was never built for." },
      { q: "Can I import my current sellers in?",
        a: "Yes — onboarding is a 20-minute call. We help you set up your branding, walk you through the admin portal, and add your active sellers together. Most agents are sending their first invite the same day." },
      { q: "What does it cost? Is there a contract?",
        a: "Your first listing is free, start to finish. After that it is $49 a month per agent, cancel anytime, no setup fees, no annual contract. Annual plans get a discount: two months free." },
      { q: "What if I have a team or assistant?",
        a: "Add a co-owner to any seller's account so a buyer's spouse, partner, or your TC sees the same plan and updates." },
      { q: "Will this actually help me win more listings?",
        a: "That's the bet most agents make first — and yes. Walk into a listing appointment, hand the seller their phone, and show them what their first-week prep guide will look like in your branded portal. You're no longer the agent who promises to be organized — you're the agent who already is." },
      { q: "How long until my sellers see results?",
        a: "Same week. You'll save the most time on questions like 'what should I do next?' and 'when's the photographer coming?' — a seller can find those answers in their portal instead of waiting on you to resend them." },
    ],
  },
  waitlist: {
    overline: "See it in action",
    headline: "See how PorchLight would work",
    headline_italic: "with your listings.",
    body: "We'll walk you through PorchLight and help you set up your portal. Two minutes to fill out, zero pressure.",
    pain_placeholder: "The same questions on repeat… disorganized text threads… clients losing track of important dates…",
    submit_label: "See how it would work",
    privacy_note: "Your info stays with us — no spam, no third parties, ever.",
    success_headline: "Your request is in.",
    success_body: "We'll email you within a day to set up a 20-minute walk-through and get you early-bird pricing.\n— The PorchLight team",
  },
  footer: {
    tagline: "PorchLight",
  },
  category: {
    overline: "A different category",
    eyebrow: "PorchLight isn't another CRM.",
    headline: "It's a",
    headline_emphasis: "guided seller experience system.",
    body: "Most agent tools are built for the agent — pipelines, contacts, transactions. PorchLight is built for the moment your seller picks up their phone at 9pm wondering what's next. Different problem. Different category.",
    not_label: "PorchLight is NOT",
    not_items: [
      "A CRM",
      "A lead tracker",
      "A pipeline manager",
      "A database",
    ],
    not_footnote: "Those tools already exist. Keep using yours.",
    is_label: "PorchLight IS",
    is_items: [
      "A client and agent experience platform",
      "A guided seller experience system",
      "Where your client reads, fills and signs",
    ],
    is_footnote: "Built for the seller side of the transaction your CRM was never designed for.",
  },
};

/**
 * Deep-merge marketing overrides on top of defaults so a partially-saved
 * document never breaks the page. Arrays are replaced wholesale (not merged
 * by index) so an admin can add/remove items freely.
 */
export function mergeMarketing(overrides) {
  if (!overrides || typeof overrides !== "object") return DEFAULT_MARKETING;
  const out = {};
  for (const k of Object.keys(DEFAULT_MARKETING)) {
    const def = DEFAULT_MARKETING[k];
    const ov = overrides[k];
    if (ov == null) { out[k] = def; continue; }
    if (Array.isArray(def)) {
      out[k] = Array.isArray(ov) && ov.length > 0 ? ov : def;
    } else if (typeof def === "object") {
      out[k] = { ...def, ...ov };
      // Re-merge any inner array sub-fields where ov sent an empty array we
      // want to keep the override (e.g. admin clears all FAQ items on purpose).
      for (const sub of Object.keys(out[k])) {
        if (Array.isArray(def[sub]) && Array.isArray(ov[sub])) out[k][sub] = ov[sub];
      }
    } else {
      out[k] = ov;
    }
  }
  return out;
}

/**
 * MarketingProvider — fetches /api/marketing-content once on mount and
 * exposes the deep-merged copy as `content` plus the raw saved overrides
 * (`overrides`) so the admin editor can show what's been customized vs
 * what's still on defaults.
 */
const MarketingCtx = createContext({
  content: DEFAULT_MARKETING,
  overrides: {},
  loading: false,
  refresh: async () => {},
});

export function MarketingProvider({ children }) {
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/marketing-content");
      setOverrides(data?.content || {});
    } catch {
      setOverrides({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const content = mergeMarketing(overrides);
  return (
    <MarketingCtx.Provider value={{ content, overrides, loading, refresh }}>
      {children}
    </MarketingCtx.Provider>
  );
}

export function useMarketing() {
  return useContext(MarketingCtx);
}

